import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Spinner, Form, Button, Table, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser, faEnvelope, faPhone, faMapMarkerAlt, faBriefcase,
  faGraduationCap, faFileAlt, faCheckCircle, faExclamationTriangle,
  faSyncAlt, faSearch, faFilter, faFileCsv, faTimes,
  faChevronRight, faDollarSign, faUsers,
  faFileInvoice, faSave, faPrint, faBuilding, faCalendarAlt, faTag,
  faClock // nouveau
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';

// --- Cache Dexie (24h) ---
const db = new Dexie('CandidatsPlacesCache');
db.version(1).stores({ candidats: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCached(key) {
  try {
    const entry = await db.candidats.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCached(key, data) {
  await db.candidats.put({ key, data, timestamp: Date.now() });
}

const API_URL = `${API_CONFIG2.apiUrl}placement/api_candidats_places.php`;
const API_FACTURE_URL = `${API_CONFIG2.apiUrl}placement/api_facture_placement.php`;

const CandidatsPlaces = ({ entrepriseId }) => {
  const [candidats, setCandidats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('cache');

  const [searchTerm, setSearchTerm] = useState('');
  const [filterContrat, setFilterContrat] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [expandedOffres, setExpandedOffres] = useState({});

  const [showFactureModal, setShowFactureModal] = useState(false);
  const [selectedOffreForFacture, setSelectedOffreForFacture] = useState(null);
  const [factureData, setFactureData] = useState({
    date_facture: '',
    reference: '',
    tva: 16
  });
  const [savingFacture, setSavingFacture] = useState(false);
  const [factureMessage, setFactureMessage] = useState(null);

  // --- Nouveaux états pour les heures supplémentaires ---
  const [heureSup, setHeureSup] = useState('non');
  const [montantSup, setMontantSup] = useState(0);

  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  const fetchCandidats = useCallback(async (forceRefresh = false) => {
    if (!entrepriseId) {
      setError('ID entreprise manquant');
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);

    const cacheKey = `candidats_entreprise_${entrepriseId}`;

    try {
      if (!forceRefresh) {
        const cached = await getCached(cacheKey);
        if (cached && Array.isArray(cached)) {
          setCandidats(cached);
          setDataSource('cache');
          setLoading(false);
          setRefreshing(false);
          return;
        }
      }

      const url = `${API_URL}?action=liste&id_entreprise=${entrepriseId}&unique=${new Date().getTime()}`;
      const response = await fetch(url, { signal: controller.signal });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Le serveur a renvoyé une réponse non-JSON : ${text.substring(0, 100)}`);
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      if (data.status === 'success') {
        const candidatsData = Array.isArray(data.data) ? data.data : [];
        setCandidats(candidatsData);
        setDataSource(forceRefresh ? 'live (actualisé)' : 'live');
        await setCached(cacheKey, candidatsData);
        setError(null);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message);
      setCandidats([]);
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [entrepriseId]);

  useEffect(() => {
    isMounted.current = true;
    fetchCandidats();
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchCandidats]);

  const filteredCandidats = useMemo(() => {
    if (!Array.isArray(candidats)) return [];
    return candidats.filter(c => {
      const searchLower = searchTerm.toLowerCase();
      const matchSearch = !searchTerm ||
        (c.nom || '').toLowerCase().includes(searchLower) ||
        (c.prenom || '').toLowerCase().includes(searchLower) ||
        (c.email || '').toLowerCase().includes(searchLower) ||
        (c.tel || '').includes(searchTerm);

      const matchContrat = !filterContrat || (c.type_contrat === filterContrat);
      const matchStatut = !filterStatut || (c.statut_candidat || '').toLowerCase() === filterStatut.toLowerCase();

      return matchSearch && matchContrat && matchStatut;
    });
  }, [candidats, searchTerm, filterContrat, filterStatut]);

  const groupedOffres = useMemo(() => {
    if (!Array.isArray(filteredCandidats)) return [];
    
    const groups = {};
    filteredCandidats.forEach(c => {
      const key = c.offre_id;
      if (!groups[key]) {
        groups[key] = {
          offre_id: key,
          offre_titre: c.offre_titre || c.offre_description || 'Offre sans titre',
          type_contrat: c.type_contrat || 'N/A',
          salaire_total: 0,
          candidats: [],
          nom_entreprise: c.nom_entreprise || 'Entreprise'
        };
      }
      groups[key].candidats.push(c);
      groups[key].salaire_total += parseFloat(c.salaire) || 0;
    });
    return Object.values(groups);
  }, [filteredCandidats]);

  const contratTypes = useMemo(() => {
    if (!Array.isArray(candidats)) return [];
    return [...new Set(candidats.map(c => c.type_contrat).filter(Boolean))];
  }, [candidats]);

  const exportCSV = () => {
    const dataToExport = filteredCandidats || [];
    if (dataToExport.length === 0) {
      alert('Aucun candidat à exporter.');
      return;
    }
    const headers = ['Offre', 'Type contrat', 'Nom', 'Prénom', 'Email', 'Téléphone', 'Salaire', 'Date placement'];
    const rows = dataToExport.map(c => [
      c.offre_titre || c.offre_description || '',
      c.type_contrat || '',
      c.nom || '',
      c.prenom || '',
      c.email || '',
      c.tel || '',
      c.salaire || '',
      c.date_placement || ''
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `candidats_places_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterContrat('');
    setFilterStatut('');
  };

  const toggleOffre = (offreId) => {
    setExpandedOffres(prev => ({
      ...prev,
      [offreId]: !prev[offreId]
    }));
  };

  const openFactureModal = (groupe) => {
    setSelectedOffreForFacture(groupe);
    const today = new Date().toISOString().slice(0,10);
    const ref = `FACT-${today.replace(/-/g,'')}-${groupe.offre_id}`;
    setFactureData({
      date_facture: today,
      reference: ref,
      tva: 16
    });
    setFactureMessage(null);
    // Réinitialiser les champs heures supplémentaires
    setHeureSup('non');
    setMontantSup(0);
    setShowFactureModal(true);
  };

  const closeFactureModal = () => {
    setShowFactureModal(false);
    setSelectedOffreForFacture(null);
    setFactureMessage(null);
  };

  const handleFactureChange = (e) => {
    const { name, value } = e.target;
    setFactureData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveFacture = async () => {
    if (!selectedOffreForFacture) return;
    setSavingFacture(true);
    setFactureMessage(null);

    const montant_ht_base = selectedOffreForFacture.salaire_total || 0;
    const montant_sup_value = heureSup === 'oui' ? (parseFloat(montantSup) || 0) : 0;
    const montant_ht = montant_ht_base + montant_sup_value;
    const tva = parseFloat(factureData.tva) || 16;
    const montant_ttc = montant_ht * (1 + tva / 100);

    const payload = {
      id_entreprise: entrepriseId,
      id_offre: selectedOffreForFacture.offre_id,
      date_facture: factureData.date_facture,
      reference: factureData.reference,
      montant_ht: montant_ht,
      tva: tva,
      montant_ttc: montant_ttc,
      heure_sup: heureSup,
      montant_sup: montant_sup_value,
      details: {
        offre_titre: selectedOffreForFacture.offre_titre,
        type_contrat: selectedOffreForFacture.type_contrat,
        candidats: selectedOffreForFacture.candidats.map(c => ({
          id: c.id,
          nom: c.nom,
          prenom: c.prenom,
          salaire: c.salaire
        }))
      }
    };

    try {
      const response = await fetch(`${API_FACTURE_URL}?action=create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.status === 'success') {
        setFactureMessage({ type: 'success', text: `Facture ${data.data.reference} enregistrée avec succès !` });
        setTimeout(closeFactureModal, 2500);
      } else {
        setFactureMessage({ type: 'danger', text: data.message || 'Erreur lors de l\'enregistrement' });
      }
    } catch (err) {
      setFactureMessage({ type: 'danger', text: 'Erreur réseau – veuillez réessayer' });
    } finally {
      setSavingFacture(false);
    }
  };

  const handlePrintFacture = () => {
    window.print();
  };

  // --- Calculs sécurisés ---
  const totalCandidats = (groupedOffres || []).reduce((acc, g) => acc + (g.candidats ? g.candidats.length : 0), 0);
  const totalSalaires = (groupedOffres || []).reduce((acc, g) => acc + (g.salaire_total || 0), 0);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des candidats placés...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4 text-danger">
        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-2" />
        <p>{error}</p>
        <button className="btn btn-outline-primary" onClick={() => fetchCandidats(true)}>
          <FontAwesomeIcon icon={faSyncAlt} className="me-2" /> Réessayer
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* === Styles généraux (inchangés) === */
        .candidats-places-wrapper { margin-top: 1.5rem; }
        .header-actions { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem; margin-bottom: 1.5rem; }
        .header-actions h5 { font-weight: 700; color: #e0e7ff; margin: 0; }
        .badge-source { font-size: 0.7rem; padding: 4px 12px; border-radius: 30px; background: rgba(59,130,246,0.15); color: #3b82f6; border: 1px solid rgba(59,130,246,0.12); }
        .filters-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem; background: rgba(255,255,255,0.03); backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.06); border-radius: 60px; padding: 0.6rem 1.2rem; margin-bottom: 1.8rem; }
        .filters-bar .search-input { flex: 1; min-width: 160px; background: transparent; border: none; color: #e0e7ff; outline: none; padding: 6px 0; font-size: 0.9rem; }
        .filters-bar .search-input::placeholder { color: #64748b; }
        .filters-bar .filter-select { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); border-radius: 40px; color: #e0e7ff; padding: 6px 14px; font-size: 0.85rem; outline: none; cursor: pointer; }
        .filters-bar .filter-select option { background: #0b1428; }
        .filters-bar .reset-btn { background: transparent; border: none; color: #94a3b8; cursor: pointer; padding: 4px 10px; border-radius: 30px; transition: 0.2s; }
        .filters-bar .reset-btn:hover { background: rgba(255,255,255,0.05); color: #e0e7ff; }
        .filters-bar .action-btn { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 40px; padding: 6px 16px; color: #e0e7ff; transition: 0.2s; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
        .filters-bar .action-btn:hover { background: rgba(255,255,255,0.08); }
        .offre-table { width: 100%; border-collapse: collapse; background: rgba(255,255,255,0.03); border-radius: 20px; overflow: hidden; backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.06); }
        .offre-table th { padding: 12px 16px; background: rgba(255,255,255,0.05); color: #94a3b8; font-weight: 600; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid rgba(255,255,255,0.06); }
        .offre-table td { padding: 10px 16px; border-bottom: 1px solid rgba(255,255,255,0.04); color: #e0e7ff; vertical-align: middle; }
        .offre-table .offre-row { cursor: pointer; transition: background 0.2s; }
        .offre-table .offre-row:hover { background: rgba(255,255,255,0.04); }
        .offre-table .offre-row .toggle-icon { margin-right: 8px; color: #94a3b8; transition: transform 0.2s; display: inline-block; }
        .offre-table .offre-row .toggle-icon.open { transform: rotate(90deg); }
        .offre-table .details-row td { padding: 0; background: rgba(255,255,255,0.02); }
        .offre-table .details-row .inner-table { width: 100%; border-collapse: collapse; }
        .offre-table .details-row .inner-table th { background: rgba(255,255,255,0.02); padding: 6px 16px; font-size: 0.7rem; color: #64748b; border-bottom: 1px solid rgba(255,255,255,0.04); }
        .offre-table .details-row .inner-table td { padding: 6px 16px; font-size: 0.85rem; color: #cbd5e1; border-bottom: 1px solid rgba(255,255,255,0.02); }
        .offre-table .details-row .inner-table tr:last-child td { border-bottom: none; }
        .offre-table .total-row td { background: rgba(255,255,255,0.04); font-weight: 600; border-top: 2px solid rgba(255,255,255,0.08); }
        .count-badge { background: rgba(255,255,255,0.05); border-radius: 30px; padding: 2px 10px; font-size: 0.75rem; color: #94a3b8; }
        .text-success { color: #4ade80; }
        .text-primary { color: #3b82f6; }

        /* ============================================================
           STYLES AMÉLIORÉS DU MODAL FACTURE (DARK GLASSMORPHISM)
           ============================================================ */
        .facture-modal .modal-content {
          background: rgba(10, 20, 45, 0.92) !important;
          backdrop-filter: blur(32px) !important;
          -webkit-backdrop-filter: blur(32px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 28px !important;
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.8), inset 0 1px 0 rgba(255,255,255,0.05) !important;
          color: #e0e7ff !important;
          overflow: hidden;
        }

        .facture-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.08) !important;
          padding: 1.2rem 1.8rem !important;
          background: rgba(255,255,255,0.02) !important;
        }

        .facture-modal .modal-header .modal-title {
          font-weight: 700;
          font-size: 1.4rem;
          display: flex;
          align-items: center;
          gap: 12px;
          color: #f0f4ff;
        }

        .facture-modal .modal-header .modal-title .icon {
          color: #3b82f6;
        }

        .facture-modal .btn-close {
          filter: invert(1) brightness(200%) !important;
          opacity: 0.6 !important;
          transition: opacity 0.2s !important;
        }
        .facture-modal .btn-close:hover {
          opacity: 0.9 !important;
        }

        .facture-modal .modal-body {
          padding: 1.8rem !important;
          max-height: 70vh;
          overflow-y: auto;
        }

        .facture-modal .modal-body::-webkit-scrollbar {
          width: 6px;
        }
        .facture-modal .modal-body::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.03);
          border-radius: 10px;
        }
        .facture-modal .modal-body::-webkit-scrollbar-thumb {
          background: #3b82f6;
          border-radius: 10px;
        }

        .facture-modal .form-label {
          color: #94a3b8;
          font-weight: 500;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .facture-modal .form-control,
        .facture-modal .form-select {
          background: rgba(255,255,255,0.04) !important;
          border: 1px solid rgba(255,255,255,0.06) !important;
          color: #e0e7ff !important;
          border-radius: 14px !important;
          padding: 10px 14px !important;
          font-size: 0.95rem !important;
          transition: border 0.2s, box-shadow 0.2s !important;
        }

        .facture-modal .form-control:focus,
        .facture-modal .form-select:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.15) !important;
          background: rgba(255,255,255,0.06) !important;
        }

        .facture-modal .form-control::placeholder {
          color: #64748b;
        }

        /* Carte récapitulative */
        .facture-recap {
          background: rgba(255,255,255,0.03);
          border-radius: 20px;
          padding: 1.2rem 1.5rem;
          border: 1px solid rgba(255,255,255,0.04);
          margin-top: 1.2rem;
        }

        .facture-recap .recap-title {
          font-size: 1rem;
          font-weight: 600;
          color: #3b82f6;
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 0.8rem;
          border-bottom: 1px solid rgba(255,255,255,0.04);
          padding-bottom: 0.5rem;
        }

        .facture-recap .recap-row {
          display: flex;
          justify-content: space-between;
          padding: 0.3rem 0;
          font-size: 0.9rem;
          color: #cbd5e1;
        }

        .facture-recap .recap-row .label {
          color: #94a3b8;
        }

        .facture-recap .recap-row .value {
          font-weight: 500;
          color: #f0f4ff;
        }

        .facture-recap .recap-table {
          width: 100%;
          margin: 0.8rem 0;
          font-size: 0.85rem;
          border-collapse: collapse;
        }

        .facture-recap .recap-table th {
          text-align: left;
          padding: 6px 4px;
          color: #94a3b8;
          font-weight: 500;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }

        .facture-recap .recap-table td {
          padding: 6px 4px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          color: #e0e7ff;
        }

        .facture-recap .recap-table tr:last-child td {
          border-bottom: none;
        }

        .facture-totals {
          border-top: 1px solid rgba(255,255,255,0.06);
          padding-top: 0.8rem;
          margin-top: 0.5rem;
        }

        .facture-totals .total-line {
          display: flex;
          justify-content: space-between;
          padding: 0.2rem 0;
          font-size: 0.95rem;
        }

        .facture-totals .total-line .label {
          color: #94a3b8;
        }

        .facture-totals .total-line .amount {
          font-weight: 600;
        }

        .facture-totals .total-line .amount.ht {
          color: #e0e7ff;
        }

        .facture-totals .total-line .amount.ttc {
          color: #4ade80;
          font-size: 1.1rem;
        }

        .facture-totals .total-line .amount.tva {
          color: #f59e0b;
        }

        /* Message d'état */
        .facture-message {
          margin-top: 1.2rem;
          padding: 0.8rem 1.2rem;
          border-radius: 16px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.04);
          display: flex;
          align-items: center;
          gap: 10px;
          animation: fadeSlideIn 0.3s ease;
        }

        .facture-message.success {
          border-color: rgba(34,197,94,0.2);
          background: rgba(34,197,94,0.05);
        }

        .facture-message.danger {
          border-color: rgba(239,68,68,0.2);
          background: rgba(239,68,68,0.05);
        }

        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .facture-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.08) !important;
          padding: 1rem 1.8rem !important;
          background: rgba(255,255,255,0.01) !important;
        }

        .facture-modal .btn {
          border-radius: 40px !important;
          padding: 0.5rem 1.6rem !important;
          font-weight: 500 !important;
          transition: all 0.2s ease !important;
        }

        .facture-modal .btn-secondary {
          background: rgba(255,255,255,0.04) !important;
          border: 1px solid rgba(255,255,255,0.06) !important;
          color: #e0e7ff !important;
        }

        .facture-modal .btn-secondary:hover {
          background: rgba(255,255,255,0.08) !important;
        }

        .facture-modal .btn-primary {
          background: #3b82f6 !important;
          border: none !important;
          color: #fff !important;
          box-shadow: 0 6px 20px rgba(59,130,246,0.25) !important;
        }

        .facture-modal .btn-primary:hover:not(:disabled) {
          background: #1d4ed8 !important;
          box-shadow: 0 8px 28px rgba(59,130,246,0.4) !important;
        }

        .facture-modal .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .facture-modal .btn-outline-info {
          background: transparent !important;
          border: 1px solid rgba(59,130,246,0.2) !important;
          color: #3b82f6 !important;
        }

        .facture-modal .btn-outline-info:hover {
          background: rgba(59,130,246,0.05) !important;
        }

        /* Impression */
        @media print {
          .facture-modal .modal-content {
            border: none !important;
            box-shadow: none !important;
            background: white !important;
            color: #000 !important;
          }
          .facture-modal .modal-header,
          .facture-modal .modal-footer,
          .facture-modal .btn,
          .facture-modal .btn-close {
            display: none !important;
          }
          .facture-modal .modal-body {
            padding: 1rem !important;
            max-height: none !important;
            overflow: visible !important;
          }
          .facture-modal .form-control,
          .facture-modal .form-label {
            color: #000 !important;
          }
          .facture-recap {
            background: #f8fafc !important;
            border: 1px solid #ddd !important;
          }
          .facture-recap .recap-title {
            color: #1e293b !important;
          }
          .facture-recap .recap-row .value {
            color: #000 !important;
          }
          .facture-recap .recap-table td {
            color: #000 !important;
          }
          .facture-totals .total-line .amount.ttc {
            color: #16a34a !important;
          }
        }

        @media (max-width: 768px) {
          .filters-bar {
            border-radius: 20px;
            padding: 0.8rem;
            flex-direction: column;
            align-items: stretch;
          }
          .filters-bar .search-input { width: 100%; }
          .filters-bar .filter-select { width: 100%; }
          .offre-table { font-size: 0.8rem; }
          .offre-table th, .offre-table td { padding: 6px 8px; }

          .facture-modal .modal-body {
            padding: 1.2rem !important;
          }
          .facture-modal .modal-header {
            padding: 1rem 1.2rem !important;
          }
          .facture-modal .modal-footer {
            flex-direction: column-reverse;
            gap: 0.5rem;
          }
          .facture-modal .modal-footer .btn {
            width: 100%;
            justify-content: center;
          }
          .facture-recap {
            padding: 0.8rem 1rem;
          }
        }
      `}</style>

      <div className="candidats-places-wrapper">
        <div className="header-actions">
          <h5>
            <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
            Candidats placés par offre
            <span className="ms-2 count-badge">{totalCandidats} candidats</span>
          </h5>
          <div className="d-flex gap-2 align-items-center">
            <span className="badge-source">{dataSource === 'cache' ? '📦 Cache' : '🔄 Live'}</span>
            <button className="action-btn btn btn-success" onClick={() => fetchCandidats(true)} disabled={refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} className="me-1" />
              {refreshing ? '...' : 'Actualiser'}
            </button>
          </div>
        </div>

        <div className="filters-bar">
          <FontAwesomeIcon icon={faSearch} className="text-secondary" />
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher (nom, prénom, email...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FontAwesomeIcon icon={faFilter} className="text-secondary ms-1" />
          <select
            className="filter-select"
            value={filterContrat}
            onChange={(e) => setFilterContrat(e.target.value)}
          >
            <option value="">Tous contrats</option>
            {contratTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          {(searchTerm || filterContrat || filterStatut) && (
            <button className="reset-btn" onClick={resetFilters} title="Réinitialiser les filtres">
              <FontAwesomeIcon icon={faTimes} className="me-1" /> Effacer
            </button>
          )}
          <button className="action-btn" onClick={exportCSV}>
            <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
          </button>
        </div>

        {groupedOffres.length === 0 ? (
          <div className="text-center py-4 text-secondary">
            <FontAwesomeIcon icon={faUser} size="3x" className="mb-3 opacity-25" />
            <p>Aucun candidat placé correspondant aux critères.</p>
          </div>
        ) : (
          <table className="offre-table">
            <thead>
              <tr>
                <th style={{ width: '5%' }}></th>
                <th style={{ width: '25%' }}>Offre</th>
                <th style={{ width: '15%' }}>Type de contrat</th>
                <th style={{ width: '12%' }}>Candidats</th>
                <th style={{ width: '18%' }}>Salaire total</th>
                <th style={{ width: '20%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groupedOffres.map((groupe) => {
                const isExpanded = expandedOffres[groupe.offre_id] || false;
                return (
                  <React.Fragment key={groupe.offre_id}>
                    <tr className="offre-row" onClick={() => toggleOffre(groupe.offre_id)}>
                      <td>
                        <span className={`toggle-icon ${isExpanded ? 'open' : ''}`}>
                          <FontAwesomeIcon icon={faChevronRight} />
                        </span>
                      </td>
                      <td><strong>{groupe.offre_titre}</strong></td>
                      <td>{groupe.type_contrat}</td>
                      <td>{groupe.candidats.length}</td>
                      <td className="text-success">{groupe.salaire_total.toFixed(2)} USD</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="action-btn btn btn-primary me-1"
                          style={{ padding: '2px 10px' }}
                          onClick={(e) => { e.stopPropagation(); openFactureModal(groupe); }}
                        >
                          <FontAwesomeIcon icon={faFileInvoice} /> Facturer
                        </button>
                        <button
                          className="action-btn btn btn-secondary"
                          style={{ padding: '2px 10px' }}
                          onClick={(e) => { e.stopPropagation(); toggleOffre(groupe.offre_id); }}
                        >
                          {isExpanded ? 'Masquer' : 'Détails'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="details-row">
                        <td colSpan="6" style={{ padding: 0 }}>
                          <table className="inner-table">
                            <thead>
                              <tr>
                                <th>Candidat</th>
                                <th>Email</th>
                                <th>Téléphone</th>
                                <th>Salaire</th>
                                <th>Date placement</th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupe.candidats.map(c => (
                                <tr key={c.id}>
                                  <td>{c.prenom} {c.nom}</td>
                                  <td>{c.email || '—'}</td>
                                  <td>{c.tel || '—'}</td>
                                  <td>{c.salaire ? c.salaire + ' USD' : 'NC'}</td>
                                  <td>{c.date_placement ? new Date(c.date_placement).toLocaleDateString() : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              <tr className="total-row">
                <td colSpan="3" style={{ textAlign: 'right', fontWeight: 'bold' }}>TOTAUX :</td>
                <td>{totalCandidats}</td>
                <td className="text-success" style={{ fontWeight: 'bold' }}>{totalSalaires.toFixed(2)} USD</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* ============================================================
          MODAL FACTURE AMÉLIORÉ – AVEC HEURES SUPPLÉMENTAIRES
          ============================================================ */}
      <Modal
        show={showFactureModal}
        onHide={closeFactureModal}
        size="lg"
        centered
        className="facture-modal"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faFileInvoice} className="icon" />
            Générer une facture
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOffreForFacture && (
            <>
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-secondary" />
                      Date de facture
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="date_facture"
                      value={factureData.date_facture}
                      onChange={handleFactureChange}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>
                      <FontAwesomeIcon icon={faTag} className="me-1 text-secondary" />
                      Référence
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="reference"
                      value={factureData.reference}
                      onChange={handleFactureChange}
                    />
                  </Form.Group>
                </div>
              </div>

              {/* ---- CHAMPS HEURES SUPPLÉMENTAIRES ---- */}
              <div className="row g-3 mt-2">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>
                      <FontAwesomeIcon icon={faClock} className="me-1 text-secondary" />
                      Heures supplémentaires
                    </Form.Label>
                    <Form.Select
                      value={heureSup}
                      onChange={(e) => {
                        setHeureSup(e.target.value);
                        if (e.target.value === 'non') setMontantSup(0);
                      }}
                    >
                      <option value="non">Non</option>
                      <option value="oui">Oui</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                {heureSup === 'oui' && (
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>
                        <FontAwesomeIcon icon={faDollarSign} className="me-1 text-secondary" />
                        Montant HS (USD)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Saisir le montant"
                        value={montantSup}
                        onChange={(e) => setMontantSup(parseFloat(e.target.value) || 0)}
                      />
                    </Form.Group>
                  </div>
                )}
              </div>

              <div className="facture-recap">
                <div className="recap-title">
                  <FontAwesomeIcon icon={faBuilding} />
                  Récapitulatif de l'offre
                </div>

                <div className="recap-row">
                  <span className="label">Entreprise</span>
                  <span className="value">{selectedOffreForFacture.nom_entreprise || 'Non définie'}</span>
                </div>
                <div className="recap-row">
                  <span className="label">Offre</span>
                  <span className="value">{selectedOffreForFacture.offre_titre}</span>
                </div>
                <div className="recap-row">
                  <span className="label">Type de contrat</span>
                  <span className="value">{selectedOffreForFacture.type_contrat}</span>
                </div>
                <div className="recap-row">
                  <span className="label">Nombre de candidats</span>
                  <span className="value">{selectedOffreForFacture.candidats.length}</span>
                </div>

                <table className="recap-table">
                  <thead>
                    <tr>
                      <th>Candidat</th>
                      <th style={{ textAlign: 'right' }}>Salaire (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOffreForFacture.candidats.map(c => (
                      <tr key={c.id}>
                        <td>{c.prenom} {c.nom}</td>
                        <td style={{ textAlign: 'right' }}>{parseFloat(c.salaire).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {heureSup === 'oui' && parseFloat(montantSup) > 0 && (
                  <div className="recap-row" style={{ borderTop: '1px dashed rgba(255,255,255,0.1)', paddingTop: '0.5rem', marginTop: '0.3rem' }}>
                    <span className="label">Heures supplémentaires</span>
                    <span className="value" style={{ color: '#fbbf24' }}>+ {parseFloat(montantSup).toFixed(2)} USD</span>
                  </div>
                )}

                <div className="facture-totals">
                  <div className="total-line">
                    <span className="label">Montant HT</span>
                    <span className="amount ht">
                      {(() => {
                        const base = selectedOffreForFacture.salaire_total || 0;
                        const sup = heureSup === 'oui' ? (parseFloat(montantSup) || 0) : 0;
                        return (base + sup).toFixed(2);
                      })()} USD
                    </span>
                  </div>
                  <div className="total-line">
                    <span className="label">TVA ({factureData.tva}%)</span>
                    <span className="amount tva">
                      {(() => {
                        const base = selectedOffreForFacture.salaire_total || 0;
                        const sup = heureSup === 'oui' ? (parseFloat(montantSup) || 0) : 0;
                        const ht = base + sup;
                        return ((ht * factureData.tva) / 100).toFixed(2);
                      })()} USD
                    </span>
                  </div>
                  <div className="total-line" style={{ borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '0.5rem', marginTop: '0.2rem' }}>
                    <span className="label" style={{ fontWeight: 'bold' }}>Total TTC</span>
                    <span className="amount ttc">
                      {(() => {
                        const base = selectedOffreForFacture.salaire_total || 0;
                        const sup = heureSup === 'oui' ? (parseFloat(montantSup) || 0) : 0;
                        const ht = base + sup;
                        return (ht * (1 + factureData.tva / 100)).toFixed(2);
                      })()} USD
                    </span>
                  </div>
                </div>
              </div>

              {factureMessage && (
                <div className={`facture-message ${factureMessage.type}`}>
                  <FontAwesomeIcon icon={factureMessage.type === 'success' ? faCheckCircle : faExclamationTriangle} />
                  <span>{factureMessage.text}</span>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeFactureModal} className="btn-close-modal">
            Fermer
          </Button>
          <Button variant="outline-info" onClick={handlePrintFacture} disabled={savingFacture}>
            <FontAwesomeIcon icon={faPrint} className="me-2" /> Imprimer
          </Button>
          <Button variant="primary" onClick={handleSaveFacture} disabled={savingFacture}>
            {savingFacture ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" /> Enregistrement...</>
            ) : (
              <><FontAwesomeIcon icon={faSave} className="me-2" /> Enregistrer la facture</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CandidatsPlaces;